import { useEffect, useRef } from 'react';

import RyeLogo from 'components/Logo';
import MainNav from 'components/Navigation';

import { getStripePortalUrl } from 'config/constants';
import { env } from 'config/env';
import AccountSwitcher from 'components/AccountDropdown';
import { useAppDispatch, useAppSelector } from 'app/store';
import { fetchUserInfo, selectUserInfo } from 'app/store/user';
import { BillingStatus, ReactivatePlanResponse } from 'app/graphql/generated/admin/graphql';
import { Badge } from 'components/ui/badge';
import { Typography } from 'components/ui/typography';
import { cn } from 'lib/utils';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from 'components/ui/alert-dialog';
import { useMutation } from '@apollo/client';
import { REACTIVATE_PLAN } from 'app/graphql';
import { toast } from 'sonner';
import { useAuth } from 'providers/AuthProvider';
import { canWrite } from 'app/access';

interface LayoutProps {
  children: JSX.Element;
  fitted?: boolean;
}

function ReactivatePlanDialog() {
  const [reactivatePlan] = useMutation<ReactivatePlanResponse>(REACTIVATE_PLAN, {
    context: { clientName: 'admin' },
  });
  const { user } = useAuth();
  const dispatch = useAppDispatch();

  const handleReactivatePlan = async () => {
    const result = await reactivatePlan();

    if (result.errors) {
      toast.error('Failed to reactivate plan');
      return;
    }
    if (user) {
      dispatch(fetchUserInfo(user.uid, user));
      toast.success('Plan reactivated');
    }
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Typography variant="link" asChild>
          <span className="cursor-pointer">here</span>
        </Typography>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Reactivate Plan?</AlertDialogTitle>
          <AlertDialogDescription>
            Reactivating your plan will start a new billing cycle and usage will be charged at the
            end of the cycle.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Close</AlertDialogCancel>
          <AlertDialogAction onClick={handleReactivatePlan}>Reactivate</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

const envBadgeLabel = (envName: string) => envName.charAt(0).toUpperCase() + envName.slice(1);

// Shared horizontal rhythm; `fitted` opts out (e.g. GraphiQL).
const containerClass = 'mx-auto w-full max-w-[1320px] px-8';

const Layout = ({ children, fitted }: LayoutProps) => {
  const userInfo = useAppSelector(selectUserInfo);
  const canManageBilling = canWrite(userInfo, 'billing');
  // Sticky chrome (topbar + tab row). We expose its measured height as a
  // CSS variable so pages with their own sticky panes (e.g. Catalog's
  // filter rail) can pin themselves flush against the chrome without
  // hardcoding a magic number that drifts when chrome content changes.
  const chromeRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = chromeRef.current;
    if (!node) {
      return;
    }
    const apply = () => {
      document.documentElement.style.setProperty(
        '--chrome-height',
        `${node.getBoundingClientRect().height}px`,
      );
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);
  const shouldShowEnvBadge = env.VITE_NODE_ENV !== 'production';
  const shouldShowReactivateSubscriptionBanner =
    canManageBilling &&
    userInfo?.billingPeriodSettings?.__typename === 'BillingSubscription' &&
    userInfo.billingPeriodSettings.status === BillingStatus.Cancelled;
  const shouldShowInactiveSubscriptionBanner =
    canManageBilling &&
    userInfo?.billingPeriodSettings?.__typename === 'BillingSubscription' &&
    userInfo.billingPeriodSettings.status === BillingStatus.Inactive;

  return (
    <div className="flex min-h-screen flex-col bg-[var(--bg)]">
      {/* Subscription warnings still surface as a top banner — these are
          remediation prompts, not just environment context, so they need
          the higher visual weight that a header badge can't carry. */}
      {shouldShowReactivateSubscriptionBanner && (
        <div className="bg-[var(--rye-lime-soft)] text-[var(--rye-ink)]">
          <Typography variant="small" as="p" className="m-0 px-4 py-3 text-center">
            Your plan is inactive. Click <ReactivatePlanDialog /> to reactivate your plan.
          </Typography>
        </div>
      )}
      {shouldShowInactiveSubscriptionBanner && (
        <div className="bg-[var(--rye-amber-soft)] text-[var(--rye-ink)]">
          <Typography variant="small" as="p" className="m-0 px-4 py-3 text-center">
            Your plan is inactive due to non-payment. Update your payment method{' '}
            <Typography variant="link" asChild>
              <a href={getStripePortalUrl()} target="_blank" rel="noopener noreferrer">
                here
              </a>
            </Typography>
            .
          </Typography>
        </div>
      )}

      {/* Topbar + tab row — both rows stick together at the top during
          scroll. Surface-bg + hairline borders give the chrome a single
          higher-contrast band against the cream content column. */}
      <div ref={chromeRef} className="sticky top-0 z-40 bg-[var(--surface)]">
        {/* Topbar — denser (14px y) so the nav links sit on a higher-
            contrast surface. */}
        <header className="border-b border-[var(--line)]">
          <div className={cn(containerClass, 'flex items-center justify-between gap-6 py-3.5')}>
            <RyeLogo />
            <div className="flex items-center gap-3">
              {shouldShowEnvBadge && (
                <Badge variant="warn" aria-label={`Environment: ${env.VITE_NODE_ENV}`}>
                  <span className="dot" aria-hidden="true" />
                  {envBadgeLabel(env.VITE_NODE_ENV)}
                </Badge>
              )}
              <AccountSwitcher />
            </div>
          </div>
        </header>

        {/* Primary nav row. */}
        <nav className="border-b border-[var(--line)]">
          <div className={containerClass}>
            <MainNav />
          </div>
        </nav>
      </div>

      {/* Main content. `fitted` flips the main pane to full-bleed (no
          horizontal padding, no vertical padding) and lets the child fill
          the remaining viewport height — used for GraphiQL and other
          self-managing single-pane editors. */}
      <main
        className={cn(
          'flex flex-1 flex-col bg-[var(--bg)]',
          fitted ? 'w-full' : cn(containerClass, 'pb-12 pt-8'),
        )}
      >
        {children}
      </main>
    </div>
  );
};

export default Layout;
